import { ApexOptions } from "apexcharts";

// Brand identity colors (from the official swatch: green/teal/slate/mint).
// Used for single-series accents and the "lead" series in comparisons —
// the two mid-tones (green/teal) are visually too close to each other
// (normal-vision ΔE 6.7, validated) to ever appear as two different
// categories in the SAME chart, so they're never both used in one place.
export const BRAND = {
  green: "#009767", // primary accent — the flagship / "actual" value
  teal: "#00A295", // secondary accent, used alone (never beside green)
  slate: "#1C4245", // muted dark tone — pairs with aqua for 2-series comparisons
  mint: "#C6E4E1", // light tint — currently unused as a mark (too pale on white, ~1.3:1)
};

// Fixed-order categorical palette for multi-category identity charts (3-5
// slices in one chart), where brand's green/teal alone fail the separation
// checks below. Led with a brand-adjacent teal/aqua so it still reads as
// "on brand" while staying distinguishable. Validated with the data-viz
// palette validator: lightness band, chroma floor, adjacent CVD separation
// and normal-vision floor all pass — never re-order without re-validating.
export const CATEGORICAL = [
  "#1baf7a", // 1 aqua (brand-adjacent teal/green)
  "#eb6834", // 2 orange
  "#2a78d6", // 3 blue
  "#eda100", // 4 yellow
  "#e87ba4", // 5 magenta
  "#008300", // 6 green
  "#4a3aa7", // 7 violet
  "#e34948", // 8 red
];

// Reserved for state/severity, never reused as a generic series color.
export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
};

export const INK = {
  primary: "#0b0b0b",
  secondary: "#52514e",
  muted: "#898781",
  grid: "#e1e0d9",
};

const FONT_FAMILY = "IBMPlexArabic, sans-serif";

function baseChartOptions(colors: string[]): ApexOptions {
  return {
    chart: {
      fontFamily: FONT_FAMILY,
      toolbar: { show: false },
      animations: { speed: 350 },
      foreColor: INK.secondary,
    },
    colors,
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.35,
        opacityFrom: 0.95,
        opacityTo: 0.75,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: INK.grid,
      strokeDashArray: 0,
    },
    tooltip: {
      theme: "light",
      style: { fontFamily: FONT_FAMILY },
    },
    legend: {
      fontFamily: FONT_FAMILY,
      labels: { colors: INK.secondary },
      markers: { size: 6 },
      itemMargin: { horizontal: 10, vertical: 4 },
    },
  };
}

interface DonutOpts {
  showLegend?: boolean;
  totalLabel?: string;
  valueSuffix?: string;
}

export function donutOptions(
  labels: string[],
  colors: string[],
  { showLegend = true, totalLabel = "الإجمالي", valueSuffix = "" }: DonutOpts = {},
): ApexOptions {
  return {
    ...baseChartOptions(colors),
    labels,
    dataLabels: { enabled: false },
    stroke: { width: 2, colors: ["#ffffff"] },
    legend: {
      ...baseChartOptions(colors).legend,
      show: showLegend,
      position: "bottom",
    },
    tooltip: {
      ...baseChartOptions(colors).tooltip,
      y: { formatter: (val: number) => `${val}${valueSuffix}` },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: totalLabel,
              color: INK.primary,
              formatter: (w: any) =>
                w.globals.seriesTotals
                  .reduce((a: number, b: number) => a + b, 0)
                  .toLocaleString("en-US"),
            },
            value: {
              color: INK.primary,
              fontFamily: FONT_FAMILY,
              formatter: (val: string) => `${val}${valueSuffix}`,
            },
          },
        },
      },
    },
  };
}

interface BarOpts {
  categories: string[];
  colors: string[];
  seriesCount: number;
  horizontal?: boolean;
  valueSuffix?: string;
  rotateLabels?: boolean;
}

export function barOptions({
  categories,
  colors,
  seriesCount,
  horizontal = false,
  valueSuffix = "",
  rotateLabels = true,
}: BarOpts): ApexOptions {
  const base = baseChartOptions(colors);
  return {
    ...base,
    chart: { ...base.chart, stacked: false },
    plotOptions: {
      bar: {
        horizontal,
        columnWidth: seriesCount > 1 ? "55%" : "40%",
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    stroke: { show: false },
    dataLabels: { enabled: false },
    legend: { ...base.legend, show: seriesCount > 1, position: "top" },
    xaxis: {
      categories,
      labels: {
        style: { colors: INK.muted, fontFamily: FONT_FAMILY },
        rotate: rotateLabels ? -30 : 0,
        trim: true,
      },
      axisBorder: { color: INK.grid },
      axisTicks: { color: INK.grid },
    },
    yaxis: {
      labels: {
        style: { colors: INK.muted, fontFamily: FONT_FAMILY },
        formatter: (val: number) => `${Math.round(val)}${valueSuffix}`,
      },
    },
    tooltip: {
      ...base.tooltip,
      y: { formatter: (val: number) => `${val}${valueSuffix}` },
    },
  };
}
